import { useCallback, useEffect, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { bulkRenameDownload, bulkRenamePreviewWithProgress, getReplaceProfiles } from '../api/client'
import type { BulkRenameFileResult } from '../api/client'

interface FileEntry {
  file: File
  id: string
}

function fileId(f: File): string {
  return `${f.name}-${f.size}`
}

export function BulkRename() {
  const { t } = useTranslation()
  const [entries, setEntries] = useState<FileEntry[]>([])
  const [renameMode, setRenameMode] = useState<'standard' | 'parties'>('standard')
  const [applyReplacements, setApplyReplacements] = useState(false)
  const [hasActiveProfile, setHasActiveProfile] = useState(false)
  const [activeProfileName, setActiveProfileName] = useState<string | null>(null)
  const [preview, setPreview] = useState<BulkRenameFileResult[] | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewProgress, setPreviewProgress] = useState(0)
  const [previewPhase, setPreviewPhase] = useState<'upload' | 'server'>('upload')
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const animRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    getReplaceProfiles()
      .then((profiles) => {
        const active = profiles.find((p) => p.is_active)
        setHasActiveProfile(!!active)
        setActiveProfileName(active?.name ?? null)
      })
      .catch(() => {})
  }, [])

  const onDrop = useCallback((accepted: File[]) => {
    setPreview(null)
    setError(null)
    setEntries((prev) => {
      const existing = new Set(prev.map((e) => e.id))
      const nieuw = accepted
        .filter((f) => !existing.has(fileId(f)))
        .map((f) => ({ file: f, id: fileId(f) }))
      return [...prev, ...nieuw]
    })
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/xml': ['.xml'], 'application/xml': ['.xml'] },
  })

  const remove = (id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id))
    setPreview(null)
    setError(null)
  }

  const reset = () => {
    setEntries([])
    setPreview(null)
    setError(null)
  }

  const handlePreview = () => {
    if (entries.length === 0) return
    setPreviewLoading(true)
    setPreviewProgress(0)
    setPreviewPhase('upload')
    setError(null)

    // Na 82% (upload klaar): start vloeiende animatie richting 95% terwijl server verwerkt
    const startServerAnim = () => {
      setPreviewPhase('server')
      let cur = 82
      animRef.current = setInterval(() => {
        cur = Math.min(95, cur + 0.4)
        setPreviewProgress(Math.round(cur))
        if (cur >= 95 && animRef.current) clearInterval(animRef.current)
      }, 80)
    }

    bulkRenamePreviewWithProgress(
      entries.map((e) => e.file),
      (pct) => {
        setPreviewProgress(pct)
        if (pct >= 82 && !animRef.current) startServerAnim()
      },
      renameMode,
    )
      .then((res) => {
        if (animRef.current) { clearInterval(animRef.current); animRef.current = null }
        setPreviewProgress(100)
        setPreview(res)
      })
      .catch((e) => {
        if (animRef.current) { clearInterval(animRef.current); animRef.current = null }
        setError(e instanceof Error ? e.message : t('bulk_rename.unknown_error'))
      })
      .finally(() => setPreviewLoading(false))
  }

  const handleDownload = async () => {
    if (entries.length === 0) return
    setDownloadLoading(true)
    setError(null)
    try {
      await bulkRenameDownload(entries.map((e) => e.file), applyReplacements, renameMode)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('bulk_rename.unknown_error'))
    } finally {
      setDownloadLoading(false)
    }
  }

  const previewOkCount = preview?.filter((r) => !r.error).length ?? 0
  const previewErrorCount = preview?.filter((r) => !!r.error).length ?? 0

  return (
    <div className="max-w-3xl space-y-6">

      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800'
        }`}
      >
        <input {...getInputProps()} />
        <div className="text-4xl mb-3">📂</div>
        <p className="text-gray-700 dark:text-gray-300 font-medium">
          {isDragActive ? t('bulk_rename.dropzone_release') : t('bulk_rename.dropzone_drag')}
        </p>
        <p className="text-sm text-gray-500 mt-1">
          {t('bulk_rename.dropzone_hint')}
        </p>
      </div>

      {/* Bestandslijst — verborgen zodra preview beschikbaar is */}
      {entries.length > 0 && !preview && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('bulk_rename.files_selected', { count: entries.length })}
            </span>
            <button onClick={reset} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              {t('bulk_rename.clear_all')}
            </button>
          </div>
          <ul className="divide-y divide-gray-50 dark:divide-gray-700 max-h-64 overflow-y-auto">
            {entries.map((e) => (
              <li key={e.id} className="flex items-center justify-between px-4 py-2 text-sm">
                <span className="text-gray-700 dark:text-gray-300 truncate">{e.file.name}</span>
                <button
                  onClick={() => remove(e.id)}
                  className="ml-3 text-gray-300 dark:text-gray-600 hover:text-red-400 flex-shrink-0 text-base leading-none"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Preview-tabel */}
      {preview && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
            <div>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t('bulk_rename.preview_title')}</span>
              <span className="ml-3 text-xs text-green-600 font-medium">{t('bulk_rename.preview_renamed_count', { count: previewOkCount })}</span>
              {previewErrorCount > 0 && (
                <span className="ml-2 text-xs text-red-500 font-medium">{t('bulk_rename.preview_error_count', { count: previewErrorCount })}</span>
              )}
            </div>
            <button onClick={reset} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              {t('bulk_rename.clear_all')}
            </button>
          </div>
          <ul className="divide-y divide-gray-50 dark:divide-gray-700 max-h-96 overflow-y-auto">
            {preview.map((r, i) => (
              <li key={i} className={`px-4 py-2.5 text-xs ${r.error ? 'bg-red-50 dark:bg-red-900/20' : ''}`}>
                {r.error ? (
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-red-500 font-medium shrink-0">{t('bulk_rename.preview_error_label')}</span>
                      <span className="text-gray-500 dark:text-gray-400 font-mono truncate">{r.original}</span>
                    </div>
                    <div className="text-red-400 mt-0.5">{r.error}</div>
                    <div className="text-gray-400 dark:text-gray-500 mt-0.5">
                      {t('bulk_rename.preview_saved_as')} <span className="font-mono">{r.renamed}</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <span className="text-gray-300 dark:text-gray-600 shrink-0 mt-0.5">→</span>
                    <div className="min-w-0">
                      <div className="text-gray-400 dark:text-gray-500 truncate font-mono">{r.original}</div>
                      <div className="text-gray-800 dark:text-gray-200 font-mono font-medium truncate">{r.renamed}</div>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Rename-modus */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('bulk_rename.naming_label')}</p>
        <div className="flex flex-col gap-2">
          {(
            [
              {
                value: 'standard',
                label: t('bulk_rename.mode_standard_label'),
                description: t('bulk_rename.mode_standard_description'),
              },
              {
                value: 'parties',
                label: t('bulk_rename.mode_parties_label'),
                description: t('bulk_rename.mode_parties_description'),
              },
            ] as const
          ).map(({ value, label, description }) => (
            <label key={value} className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="radio"
                name="rename-mode"
                value={value}
                checked={renameMode === value}
                onChange={() => { setRenameMode(value); setPreview(null) }}
                className="mt-0.5 text-blue-600 focus:ring-blue-400"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium">{label}</span>
                <span className="ml-1.5 text-gray-400 dark:text-gray-500 text-xs">{description}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Opties */}
      {hasActiveProfile && (
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={applyReplacements}
            onChange={(e) => setApplyReplacements(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-400"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {t('bulk_rename.apply_replacements')}
            <span className="ml-1.5 text-gray-400 dark:text-gray-500">
              ({t('bulk_rename.active_profile')} <span className="font-medium text-gray-600 dark:text-gray-400">{activeProfileName}</span>)
            </span>
          </span>
        </label>
      )}

      {/* Fout */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-700 dark:text-red-300 rounded-lg px-4 py-3 text-sm flex justify-between">
          {error}
          <button onClick={() => setError(null)} className="ml-4 text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* Progress tijdens preview-laden */}
      {previewLoading && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>
              {previewPhase === 'upload'
                ? t('bulk_rename.progress_uploading', { percent: previewProgress })
                : t('bulk_rename.progress_processing', { percent: previewProgress })}
            </span>
            <span className="text-gray-400">{previewProgress}%</span>
          </div>
          <div className="w-full h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all duration-150"
              style={{ width: `${previewProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Knoppen */}
      {entries.length > 0 && (
        <div className="flex gap-3">
          {/* Voorbeeld-knop: primair zolang er nog geen preview is */}
          {!preview && (
            <button
              onClick={handlePreview}
              disabled={previewLoading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-medium py-2.5 rounded-lg text-sm transition-colors"
            >
              {previewLoading
                ? t('bulk_rename.btn_reading')
                : t('bulk_rename.btn_preview', { count: entries.length })}
            </button>
          )}

          {/* Download-knop: primair na preview, secundair daarvoor */}
          <button
            onClick={handleDownload}
            disabled={downloadLoading || previewLoading}
            className={`font-medium py-2.5 rounded-lg text-sm transition-colors ${
              preview
                ? 'flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white'
                : 'px-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50'
            }`}
          >
            {downloadLoading ? t('bulk_rename.btn_downloading') : t('bulk_rename.btn_download_zip')}
          </button>
        </div>
      )}

      {!entries.length && (
        <button
          disabled
          className="w-full bg-gray-300 text-white font-medium py-2.5 rounded-lg text-sm"
        >
          {t('bulk_rename.btn_select_files')}
        </button>
      )}
    </div>
  )
}
